/**
 * ValoraaBead header controller (<vb-header>, sections/vb-header.liquid).
 *
 * 1. Hide on scroll: with data-vb-scroll="hide" the bar slides away while the shopper scrolls
 *    down and slides back on scroll up. It stays in place near the top, while a menu is open,
 *    while it holds keyboard focus and while the pointer is over it. Works with Horizon's
 *    .page-wrapper scroller (990px and up) and the document scroller (phones), mouse and touch.
 *    With data-vb-transparent (home page setting) the bar is see-through at the very top
 *    (data-vb-top, styled in the header section) and solid once the page scrolls past TOP px.
 * 2. Heights: --header-height (bar height, read by Horizon) on <body>, and --vb-header-offset
 *    (what is currently visible: 0 when hidden) on <html> for sticky toolbars that sit under it.
 * 3. Fit: when a long admin menu does not fit beside the wordmark and icons, `data-vb-compact`
 *    switches the desktop header to the menu-button layout (the phone/tablet sheet).
 * 4. Desktop mega menu (card photos start loading when the pointer or focus reaches the nav): opens on hover (short intent delay), on click, and on Enter/Space; Escape
 *    closes and returns focus to the trigger; leaving the item, focusing elsewhere, clicking the
 *    scrim or outside closes it. aria-expanded mirrors the state; a scrim dims the page.
 */
(() => {
  if (customElements.get('vb-header')) return;

  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 180;
  const DELTA = 6;
  const TOP = 12;

  class VbHeader extends HTMLElement {
    /** @type {HTMLElement | null} */
    #openItem = null;
    #openTimer = 0;
    #closeTimer = 0;
    #last = 0;
    #frame = 0;
    #keyboard = false;
    #suppressHover = false;
    /** The open panel was opened by hovering (a click on its label then keeps it open) */
    #openedByHover = false;
    #controller = new AbortController();
    /** @type {ResizeObserver | null} */
    #resize = null;

    connectedCallback() {
      const { signal } = this.#controller;
      this.wrapper = document.querySelector('.page-wrapper');
      this.#last = this.#scrollTop();

      // ---------- Scroll ----------
      document.addEventListener(
        'scroll',
        (event) => {
          const target = event.target;
          if (target === document || target === this.wrapper || target === document.scrollingElement) this.#schedule();
        },
        { capture: true, passive: true, signal }
      );
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') this.#keyboard = true;
      }, { capture: true, passive: true, signal });
      document.addEventListener('pointerdown', () => { this.#keyboard = false; }, { capture: true, passive: true, signal });

      // ---------- Heights ----------
      this.#resize = new ResizeObserver(() => this.#publishHeights());
      this.#resize.observe(this);
      this.#publishHeights();

      // ---------- Mega menu ----------
      // Panel pictures (cards, link thumbnails, tiles) are lazy inside hidden panels: an item's
      // pictures start loading on hover/focus intent for that item only, so nothing else loads.
      const warm = (/** @type {Element} */ item) => {
        for (const img of item.querySelectorAll('.vb-mega img[loading="lazy"]')) img.loading = 'eager';
      };

      for (const item of this.querySelectorAll('.vb-hnav__item--mega')) {
        const trigger = item.querySelector('[data-vb-mega-trigger]');
        if (!trigger) continue;
        item.addEventListener('pointerenter', () => warm(item), { once: true, signal });
        item.addEventListener('focusin', () => warm(item), { once: true, signal });
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !DESKTOP.matches || this.#suppressHover) return;
          clearTimeout(this.#closeTimer);
          clearTimeout(this.#openTimer);
          const delay = this.#openItem ? 0 : OPEN_DELAY;
          this.#openTimer = window.setTimeout(() => {
            this.open(/** @type {HTMLElement} */ (item));
            this.#openedByHover = true;
          }, delay);
        }, { signal });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.#suppressHover = false;
          clearTimeout(this.#openTimer);
          this.#closeTimer = window.setTimeout(() => {
            if (this.#openItem === item) this.close();
          }, CLOSE_DELAY);
        }, { signal });
        trigger.addEventListener('click', (event) => {
          clearTimeout(this.#openTimer);
          // Hover, then the natural click on the same label: keep the panel open. Only a panel
          // opened by click or keyboard toggles shut on a second click (Esc and the scrim still close).
          const mouse = /** @type {PointerEvent} */ (event).pointerType === 'mouse' || (/** @type {MouseEvent} */ (event).detail > 0 && DESKTOP.matches && matchMedia('(hover: hover)').matches);
          if (this.#openItem === item && this.#openedByHover && mouse) {
            this.#openedByHover = false;
            return;
          }
          if (this.#openItem === item) this.close();
          else {
            this.open(/** @type {HTMLElement} */ (item));
            this.#openedByHover = false;
          }
        }, { signal });
        item.addEventListener('focusout', (event) => {
          const next = /** @type {FocusEvent} */ (event).relatedTarget;
          if (next instanceof Node && item.contains(next)) return;
          if (this.#openItem === item && next !== null) this.close();
        }, { signal });
      }

      // Escape closes an open panel wherever focus is (WCAG 1.4.13: hover content is dismissible
      // without moving the pointer); focus returns to the trigger only when it was in the header.
      window.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.#openItem) return;
        const trigger = this.#openItem.querySelector('[data-vb-mega-trigger]');
        const focusInside = this.contains(document.activeElement);
        // Only a pointer resting on the item must not re-open it; a later hover opens as usual
        const hovered = this.#openItem.matches(':hover');
        this.close();
        this.#suppressHover = hovered;
        if (focusInside && trigger instanceof HTMLElement) trigger.focus();
      }, { capture: true, signal });

      // Cart count for screen readers ("2 items"), kept in sync with the bubble Horizon updates
      const countLabel = this.querySelector('#VbCartCount');
      const bubbleCount = this.querySelector('[ref="cartBubbleCount"]');
      if (countLabel && bubbleCount) {
        const sync = () => {
          const n = parseInt(bubbleCount.textContent || '0', 10) || 0;
          const one = countLabel.getAttribute('data-one') || '';
          const other = countLabel.getAttribute('data-other') || '';
          countLabel.textContent = n === 1 ? one : other.replace('[n]', String(n));
        };
        new MutationObserver(sync).observe(bubbleCount, { childList: true, characterData: true, subtree: true });
      }

      this.querySelector('[data-vb-mega-scrim]')?.addEventListener('click', () => this.close(), { signal });
      document.addEventListener('pointerdown', (event) => {
        if (this.#openItem && event.target instanceof Node && !this.contains(event.target)) this.close();
      }, { signal });
      DESKTOP.addEventListener('change', () => this.close(), { signal });

      for (const type of ['pointerenter', 'pointerleave', 'focusin', 'focusout']) {
        this.addEventListener(type, () => setTimeout(() => this.#schedule(), 0), { signal });
      }

      // ---------- Fit ----------
      // The main menu is admin content: when its items no longer fit between the wordmark and
      // the icons (long menus, 990–1200px), the header switches to the menu-button layout.
      let fitFrame = 0;
      const scheduleFit = () => {
        if (!fitFrame) fitFrame = requestAnimationFrame(() => { fitFrame = 0; this.#fit(); });
      };
      window.addEventListener('resize', scheduleFit, { passive: true, signal });
      document.fonts?.ready.then(scheduleFit);
      this.#fit();

      this.#update();
    }

    #fit() {
      this.removeAttribute('data-vb-compact');
      if (!DESKTOP.matches) return;
      const nav = this.querySelector('.vb-hnav');
      const logo = this.querySelector('.vb-header__logo');
      const actions = this.querySelector('.vb-header__actions');
      if (!nav || !logo || !actions) return;
      const n = nav.getBoundingClientRect();
      const l = logo.getBoundingClientRect();
      // Keep at least 48px of air between the nav, the wordmark and the icons (never crowd them)
      const AIR = 48;
      // The actions box fills its grid column: measure from the first visible control instead
      const controls = [...actions.querySelectorAll('.vb-header__icon-btn, .vb-header__search-pill')].filter((el) => el.getBoundingClientRect().width > 0);
      const actionsLeft = controls.length ? Math.min(...controls.map((el) => el.getBoundingClientRect().left)) : actions.getBoundingClientRect().right;
      // Nav left of the wordmark (redesign) or between the wordmark and the icons (older layout)
      const tight = n.left < l.left
        ? n.right + AIR > l.left || l.right + AIR > actionsLeft
        : l.right + AIR > n.left || n.right + AIR > actionsLeft;
      if (tight) {
        this.close();
        this.setAttribute('data-vb-compact', '');
      }
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#resize?.disconnect();
      clearTimeout(this.#openTimer);
      clearTimeout(this.#closeTimer);
    }

    /** @param {HTMLElement} item */
    open(item) {
      if (this.#openItem === item) return;
      if (this.#openItem) this.#setItem(this.#openItem, false);
      this.#openItem = item;
      this.#setItem(item, true);
      this.toggleAttribute('data-vb-mega-open', true);
      this.removeAttribute('data-vb-hidden');
    }

    close() {
      clearTimeout(this.#openTimer);
      this.#openedByHover = false;
      if (!this.#openItem) return;
      this.#setItem(this.#openItem, false);
      this.#openItem = null;
      this.removeAttribute('data-vb-mega-open');
    }

    /** @param {HTMLElement} item @param {boolean} state */
    #setItem(item, state) {
      const trigger = item.querySelector('[data-vb-mega-trigger]');
      const panel = item.querySelector('[data-vb-mega-panel]');
      trigger?.setAttribute('aria-expanded', String(state));
      if (panel instanceof HTMLElement) panel.hidden = !state;
    }

    #scrollTop() {
      return Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, this.wrapper ? this.wrapper.scrollTop : 0);
    }

    #schedule() {
      if (!this.#frame) this.#frame = requestAnimationFrame(() => this.#update());
    }

    #engaged() {
      const active = document.activeElement;
      const keyboardFocus = this.#keyboard && active instanceof Element && this.contains(active);
      const drawer = this.querySelector('[data-vb-menu-drawer]');
      return Boolean(
        this.#openItem ||
          keyboardFocus ||
          (FINE.matches && this.matches(':hover')) ||
          (drawer instanceof HTMLDialogElement && drawer.open)
      );
    }

    #update() {
      this.#frame = 0;
      const top = this.#scrollTop();
      if (this.hasAttribute('data-vb-transparent')) this.toggleAttribute('data-vb-top', top <= TOP);
      if (this.dataset.vbScroll !== 'hide' || this.#engaged() || top <= this.offsetHeight * 2) {
        this.#setHidden(false);
        this.#last = top;
        return;
      }
      const delta = top - this.#last;
      if (delta > DELTA) {
        this.#setHidden(true);
        this.#last = top;
      } else if (delta < -DELTA) {
        this.#setHidden(false);
        this.#last = top;
      }
    }

    /** @param {boolean} hidden */
    #setHidden(hidden) {
      if (this.hasAttribute('data-vb-hidden') === hidden) return;
      this.toggleAttribute('data-vb-hidden', hidden);
      this.#publishHeights();
    }

    #publishHeights() {
      const bar = this.querySelector('.vb-header__bar');
      const height = Math.round((bar instanceof HTMLElement ? bar : this).getBoundingClientRect().height);
      document.body.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty('--vb-header-offset', this.hasAttribute('data-vb-hidden') ? '0px' : `${height}px`);
      const group = document.querySelector('#header-group');
      if (group) {
        let total = 0;
        for (const child of group.children) if (child instanceof HTMLElement) total += child.offsetHeight;
        document.body.style.setProperty('--header-group-height', `${total}px`);
      }
    }
  }

  customElements.define('vb-header', VbHeader);
})();
